import { useAppSelector } from 'app/hooks'
import { InputData } from 'app/slices/news-feed.slice'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { ERRORS } from 'locales/locales'
import { useCallback, useMemo } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { FixMeLater } from 'types'
import {
  POST_SUPPORTED_IMAGES_TYPES,
  POST_SUPPORTED_VIDEO_TYPES,
  FILE_COUNT_ERROR_CODE,
  FILE_SIZE_ERROR_CODE,
  POST_SUPPORTED_FILES_TYPES,
  MB,
  FILE_INVALID_TYPE_ERROR_CODE,
} from 'utils/constants'

interface ICommonInputFilesPrpos {
  inputData: InputData
  onChangeInputFileData: (items: Array<File>) => void
  type?: string
}
const useCommonInputFiles = ({
  inputData,
  onChangeInputFileData,
  type = 'photo',
}: ICommonInputFilesPrpos) => {
  const { t } = useTranslation()
  const systemData = useAppSelector((state) => state.system.data)
  const acceptableExtensions = useMemo(
    () => systemData?.fileExtensions?.map((extension: string) => extension.toLowerCase()),
    [systemData?.fileExtensions],
  )
  
  const extensions = useCallback(
    (type: FixMeLater) => {
      if (!acceptableExtensions || acceptableExtensions.length === 0) return {}
      const result: FixMeLater = {}
      Object.entries(type).forEach(([mimeType, extensions]) => {
        const matchingExtensions = acceptableExtensions.includes(mimeType)
        if (matchingExtensions) result[mimeType] = extensions
      })
      return result
    },
    [acceptableExtensions],
  )

  const acceptFile = useCallback(
    (type: string) => {
      switch (type) {
        case 'photo':
          return extensions(POST_SUPPORTED_IMAGES_TYPES)
        case 'video':
          return extensions(POST_SUPPORTED_VIDEO_TYPES)
        case 'file':
          return extensions(POST_SUPPORTED_FILES_TYPES)
        default:
          return {}
      }
    },
    [extensions],
  )

  const fileTypesNotAllowed = useCallback(
    (fileTypes: string) => {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
          file_name: fileTypes,
        }),
      })
      throw new Error(
        t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
          file_name: fileTypes,
        }),
      )
    },[t])

  const onDropAccepted = useCallback(
    (files: Array<File>) => {
      if (
        files.length > 4 ||
        inputData.files.length > 3 ||
        files.length + inputData.files.length > 4
      ) {
        return DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.FILE_COUNT_ERROR, { count: 4 }),
        })
      }
      onChangeInputFileData(files)
    },
    [t, inputData.files, onChangeInputFileData],
  )

  const onDropRejected = useCallback(
    (fileRejections: Array<FileRejection>) => {
      if (fileRejections.some((file: FileRejection) => file?.errors?.some((error: any) => error?.code === FILE_COUNT_ERROR_CODE))) {
        DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.FILE_COUNT_ERROR, { count: 4 }),
        })
        throw new Error(t(ERRORS.FILE_COUNT_ERROR, { count: 4 }))
      }
      if (fileRejections.some((file: FileRejection) => file?.errors?.some((error: any) => error?.code === FILE_SIZE_ERROR_CODE))) {
        return DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.FILE_SIZE_ERROR, {
            fileSize: systemData?.fileSizeLimit,
            fileSizeType: 'MB',  
            file_name: fileRejections.map((file: FileRejection) => file?.file?.name)?.join(', '),
          }),
        })
      }
    },
    [t, systemData?.fileSizeLimit],
  )

  const { getInputProps, inputRef, fileRejections } = useDropzone({
    accept: acceptFile(type),
    maxFiles: 4,
    maxSize: parseInt(systemData?.fileSizeLimit ?? 50) * MB,
    onDropAccepted,
    onDropRejected,
    onDrop: (acceptedFiles, fileRejections) => {
      //TODO: Arapça eklenecek.
      if (Object.keys(acceptFile(type)).length === 0) {
        switch (type) {
          case 'photo':
            return fileTypesNotAllowed(t(ERRORS.PHOTO_FILE_TYPES_NOT_ALLOWED))
          case 'video':
            return fileTypesNotAllowed(t(ERRORS.VIDEO_FILE_TYPES_NOT_ALLOWED))
          case 'file':
            return fileTypesNotAllowed(t(ERRORS.NO_FILE_TYPES_ALLOWED))
          default:
            return fileTypesNotAllowed(t(ERRORS.NO_FILE_TYPES_ALLOWED))
        }
      }
      if (
        fileRejections?.length &&
        fileRejections?.some((file: FileRejection) =>
          file?.errors.some(
            (error: any) =>
              error?.code === FILE_COUNT_ERROR_CODE || error?.code === FILE_SIZE_ERROR_CODE,
          ),
        )
        //Hiçbir dosya tipi seçilmediğinde onDropRejected çağrılmıyor sadece onDrop çağrılıyor. Çünkü hiçbir dosya tipi verilmediğinde hepsini alıyor. Ancak bizim istediğimiz hiçbirini almaması.
      ) {
        return onDropRejected(fileRejections)
      }
      if (fileRejections && fileRejections?.length) {
        return fileTypesNotAllowed(
          fileRejections?.map((file: FileRejection) => file?.file?.name)?.join(', '),
        )
      }
    },
  })

  return {
    getInputProps,
    inputRef,
    fileRejections,
  }
}

export default useCommonInputFiles
